import type { ReactElement } from 'react'
import { lazy, Suspense, useContext } from 'react'
import React from 'react'
import { Center, Icon } from '@chakra-ui/react'
import {
  getExtensionFromFileTypeAndMimeType,
  supportedMultiPageFileFormats,
} from '../../../../utils/mediaLibrary'
import type { MediaLibrarySearchFileResponse } from '../../../interfaces'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import { VideoIcon } from '../../../../graphics/media-library/file-icons'
import { UserContext } from '../../../providers'
import CodePreview from './CodePreview'
import IconPreview from './IconPreview'
import ImagePreview from './ImagePreview'
import PDFPreview from './PDFPreview'
import TxtPreview from './TxtPreview'
import AudioPreview from './AudioPreview'

const VideoPreview = lazy(() => import('./VideoPreview'))

const LazyVideoPreview = (props: FilePreviewProps): ReactElement => (
  <Suspense
    fallback={
      <Center>
        <Icon as={VideoIcon} boxSize="100%" padding="0" />
      </Center>
    }
  >
    <VideoPreview {...props} />
  </Suspense>
)

const ExtensionToComponentMap: {
  [key: string]: React.FC<FilePreviewProps>
} = {
  jpg: ImagePreview,
  jpeg: ImagePreview,
  png: ImagePreview,
  webp: ImagePreview,
  gif: ImagePreview,
  svg: ImagePreview,
  heic: ImagePreview,
  heif: ImagePreview,
  avif: ImagePreview,
  pdf: PDFPreview,
  js: CodePreview,
  css: CodePreview,
  woff2: IconPreview,
  woff: IconPreview,
  ttf: IconPreview,
  otf: IconPreview,
  eot: IconPreview,
  txt: TxtPreview,
  mp4: LazyVideoPreview,
  m4v: LazyVideoPreview,
  webm: LazyVideoPreview,
  mov: IconPreview,
  swf: IconPreview,
  ts: IconPreview,
  m3u8: IconPreview,
  json: IconPreview,
  xml: IconPreview,
  mjs: CodePreview,
  yaml: CodePreview,
  yml: CodePreview,
  mp3: AudioPreview,
  m4a: AudioPreview,
  wav: AudioPreview,
  ai: ImagePreview,
  eps: ImagePreview,
  psd: ImagePreview,
  ept: ImagePreview,
}

const FilePreview = ({
  file,
  folderPath,
  showPreview = false,
  showThreads = false,
  transformation = [],
  disablePadding = false,
  height,
  width,
}: FilePreviewProps & { showPreview?: boolean }): ReactElement => {
  const { fileType } = file
  const { user } = useContext(UserContext)

  const extension = getExtensionFromFileTypeAndMimeType(file)

  let PreviewComponent: React.FC<FilePreviewProps> | null = null

  if (extension) {
    PreviewComponent = ExtensionToComponentMap[extension]
  }

  if (extension === null) {
    PreviewComponent = ImagePreview
  }

  if (fileType !== 'image' && !showPreview) {
    PreviewComponent = IconPreview
  }

  if (
    extension &&
    ['mp4', 'm4v', 'mov', 'webm'].includes(extension) &&
    !showPreview
  ) {
    PreviewComponent = LazyVideoPreview
  }

  // For PDF thumbnail
  if (supportedMultiPageFileFormats.includes(extension ?? '') && !showPreview) {
    PreviewComponent = ImagePreview
  }

  if (
    extension === 'mov' &&
    (user?.videoSettings?.optimization?.format?.enabled ||
      user?.videoSettings?.optimization?.quality?.enabled)
  ) {
    PreviewComponent = LazyVideoPreview
  }

  if (!PreviewComponent) {
    PreviewComponent = IconPreview
  }

  return (
    <PreviewComponent
      file={file}
      folderPath={folderPath}
      transformation={transformation}
      showPreview={showPreview}
      showThreads={showThreads}
      disablePadding={disablePadding}
      extension={extension ?? undefined}
      height={height}
      width={width}
    />
  )
}

export default FilePreview

export interface FilePreviewProps {
  file: MediaLibrarySearchFileResponse | FileDetailsResponse
  folderPath: string
  transformation?: Array<{ [key: string]: string }>
  showPreview?: boolean
  showThreads?: boolean
  disablePadding?: boolean
  extension?: string
  height?: number
  width?: number
}
